<template>
  <div class="product-ctr-wrapper h-100 d-flex flex-column justify-space-between">
    <div v-if="data && data.length > 0">
      <div v-if="showExport" class="d-flex justify-end mb-2">
        <ExportButton @click="exportData" tooltip="Export to CSV" />
      </div>

      <apexchart type="bar" :options="chartOptions" :series="series" height="280"></apexchart>
      <v-table density="comfortable" class="mt-4">
        <thead>
          <tr class="bg-grey-lighten-3">
            <th class="text-left font-weight-bold">Product</th>
            <th class="text-left font-weight-bold">Type</th>
            <th class="text-right font-weight-bold">Rec</th>
            <th class="text-right font-weight-bold">Clicks</th>
            <th class="text-right font-weight-bold">
              <span class="d-inline-flex align-center cursor-pointer" @click="toggleSortOrder">
                CTR
                <v-icon size="14" class="ml-1">{{ currentSortOrder === 'desc' ? 'mdi-arrow-down' : 'mdi-arrow-up' }}</v-icon>
              </span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(item, index) in displayedPaginatedData" :key="index" :class="['hover-row', index % 2 === 0 ? 'row-odd' : 'row-even']">
            <td class="font-weight-medium">{{ item.product }}</td>
            <td class="text-body-2">{{ item.productType }}</td>
            <td class="text-right text-body-2">{{ formatNumber(item.recommendations) }}</td>
            <td class="text-right text-body-2">{{ formatNumber(item.clicks) }}</td>
            <td class="text-right">
              <v-chip :color="getCtrColor(item.ctr)" size="small" label> {{ item.ctr.toFixed(1) }}% </v-chip>
            </td>
          </tr>
        </tbody>
      </v-table>
    </div>

    <div v-else class="d-flex flex-column align-center justify-center h-100" style="min-height: 200px">
      <v-icon size="48" color="grey-lighten-1">mdi-chart-bar</v-icon>
      <span class="text-body-2 text-medium-emphasis mt-2">No product data in selected period</span>
    </div>

    <div v-if="data && data.length > 0 && displayedTotalPages > 1" class="pagination-controls d-flex align-center justify-center mt-2 pt-2" style="border-top: 2px solid rgba(0, 0, 0, 0.06)">
      <v-btn icon size="x-small" variant="text" :disabled="displayedCurrentPage === 1" @click="displayedCurrentPage--">
        <v-icon>mdi-chevron-left</v-icon>
      </v-btn>
      <span class="text-body-2 mx-3">{{ displayedCurrentPage }} / {{ displayedTotalPages }}</span>
      <v-btn icon size="x-small" variant="text" :disabled="displayedCurrentPage === displayedTotalPages" @click="displayedCurrentPage++">
        <v-icon>mdi-chevron-right</v-icon>
      </v-btn>
    </div>
  </div>
</template>

<script>
import VueApexCharts from 'vue3-apexcharts';
import ExportButton from '@/components/TaxDashboard/ExportButton.vue';
import { exportToCSV } from '@/utils/csvExport';

export default {
  name: 'ProductCtrTable',
  components: {
    apexchart: VueApexCharts,
    ExportButton,
  },
  props: {
    data: {
      type: Array,
      required: true,
    },
    showExport: {
      type: Boolean,
      default: true,
    },
    paginatedData: {
      type: Array,
      default: null,
    },
    sortedData: {
      type: Array,
      default: null,
    },
    currentPage: {
      type: Number,
      default: 1,
    },
    totalPages: {
      type: Number,
      default: 1,
    },
    sortOrder: {
      type: String,
      default: null, // null = use internal, 'asc' or 'desc'
    },
  },
  emits: ['update:currentPage', 'update:sortOrder'],
  data() {
    return {
      internalPage: this.currentPage,
      itemsPerPage: 5,
      internalSortOrder: 'desc', // 'asc' or 'desc'
    };
  },
  computed: {
    // Use external pagination if provided, otherwise use internal
    displayedPaginatedData() {
      return this.paginatedData || this.internalPaginatedData;
    },
    displayedSortedData() {
      return this.sortedData || this.internalSortedData;
    },
    displayedCurrentPage: {
      get() {
        return this.currentPage || this.internalPage;
      },
      set(value) {
        if (this.currentPage !== undefined) {
          this.$emit('update:currentPage', value);
        } else {
          this.internalPage = value;
        }
      },
    },
    displayedTotalPages() {
      return this.totalPages || this.internalTotalPages;
    },
    currentSortOrder() {
      return this.sortOrder || this.internalSortOrder;
    },
    // Internal fallback pagination (for backward compatibility)
    enrichedData() {
      return this.data;
    },
    internalSortedData() {
      const order = this.currentSortOrder === 'asc' ? 1 : -1;
      return [...this.enrichedData].sort((a, b) => (a.ctr - b.ctr) * order);
    },
    internalPaginatedData() {
      const start = (this.internalPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.internalSortedData.slice(start, end);
    },
    internalTotalPages() {
      return Math.ceil(this.internalSortedData.length / this.itemsPerPage);
    },
    series() {
      return [
        {
          name: 'CTR %',
          data: this.displayedSortedData.map((item) => parseFloat(item.ctr.toFixed(1))),
        },
      ];
    },
    chartOptions() {
      return {
        chart: {
          type: 'bar',
          toolbar: {
            show: false,
          },
        },
        plotOptions: {
          bar: {
            horizontal: true,
            distributed: true,
            borderRadius: 4,
            dataLabels: {
              position: 'top',
            },
          },
        },
        colors: this.displayedSortedData.map((item) => this.getCtrColorHex(item.ctr)),
        dataLabels: {
          enabled: false,
        },
        xaxis: {
          categories: this.displayedSortedData.map((item) => item.product),
          labels: {
            formatter: function (val) {
              return val + '%';
            },
          },
        },
        yaxis: {
          labels: {
            style: {
              fontSize: '11px',
              fontWeight: 500,
            },
          },
        },
        legend: {
          show: false,
        },
        grid: {
          borderColor: '#f1f1f1',
        },
      };
    },
  },
  methods: {
    toggleSortOrder() {
      const newOrder = this.currentSortOrder === 'desc' ? 'asc' : 'desc';
      if (this.sortOrder !== null) {
        this.$emit('update:sortOrder', newOrder);
      } else {
        this.internalSortOrder = newOrder;
      }
    },
    formatNumber(num) {
      return num?.toLocaleString('en-US') || '0';
    },
    getCtrColor(ctr) {
      if (ctr >= 70) return 'success';
      if (ctr >= 50) return 'warning';
      return 'error';
    },
    getCtrColorHex(ctr) {
      if (ctr >= 70) return '#43A047';
      if (ctr >= 50) return '#FB8C00';
      return '#E53935';
    },
    exportData() {
      const headers = [
        { key: 'product', label: 'Product' },
        { key: 'productType', label: 'Type' },
        { key: 'recommendations', label: '# Recommendations' },
        { key: 'clicks', label: 'Clicks' },
        { key: 'ctr', label: 'CTR (%)' },
      ];
      exportToCSV(this.displayedSortedData, headers, 'product_ctr');
    },
  },
};
</script>

<style scoped>
.product-ctr-wrapper {
  width: 100%;
}

.hover-row {
  transition: background-color 0.2s ease;
}

.row-odd {
  background-color: rgba(0, 0, 0, 0.03);
  border-radius: 4px;
}

.row-even {
  background-color: rgba(255, 255, 255, 0.5);
  border-radius: 4px;
}

.hover-row:hover {
  background-color: rgba(0, 0, 0, 0.06) !important;
}

thead tr th {
  font-size: 0.75rem;
  padding: 12px 8px;
  border-bottom: 2px solid rgba(0, 0, 0, 0.12);
}

tbody tr td {
  padding: 12px 8px;
  font-size: 0.813rem;
}

.pagination-controls {
  min-height: 40px;
}

.cursor-pointer {
  cursor: pointer;
}
</style>
